import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {styled} from '@mui/material';
import MuiBox from '@mui/material/Box';
import {unstable_extendSxProp as extendSxProp} from '@mui/system';
import {useEventListener} from 'usehooks-ts';
import type {FCWithChildren} from 'types';
import {Fonts} from 'theme/fonts';
import type {BoxProps} from '../types';
import type {LayoutProps} from './types';

const MuiBoxStyled = styled(MuiBox)(({theme}) => ({
  div: {
    opacity: 1,
    transition: 'opacity 0.3s',
    '&.Ebba-hidden': {
      pointerEvents: 'none',
      opacity: 0,
    },
    '&.Ebba-focusable': {
      ...Fonts.body2,
      color: theme.colors.burgundy,
      border: 'none',
      boxShadow: `0 0 0 1.25px ${theme.colors.warmGreen}`,
      borderRadius: theme.shape.borderRadius,
      outline: 'none',
      backgroundColor: theme.colors.offWhite,
      transition: '.1s',
      paddingLeft: theme.spacing(1.75),
      paddingRight: theme.spacing(1.75),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      ':focus-within, :hover, :active': {
        boxShadow: `0 0 0 2px ${theme.colors.warmGreen}`,
        '> *': {
          border: 'none',
          padding: 0,
          outline: 0,
          WebkitAppearance: 'none',
          boxShadow: 'none',
        },
      },
      '> *': {
        border: 'none',
        padding: 0,
        '::selection': {
          background: theme.palette.background.default,
        },
        '::placeholder': {
          color: theme.colors.warmGreen,
        },
      },
    },
    '&.Ebba-focusable-error': {
      boxShadow: `0 0 0 1.25px ${theme.palette.error.main}`,
      ':focus-within, :hover, :active': {
        boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
      },
      '> *': {
        '::placeholder': {
          color: theme.colors.error40,
        },
      },
    },
    '&.Ebba-focusable-warning': {
      boxShadow: `0 0 0 1.25px ${theme.palette.warning.main}`,
      ':focus-within, :hover, :active': {
        boxShadow: `0 0 0 2px ${theme.palette.warning.main}`,
      },
      '> *': {
        '::placeholder': {
          color: `${theme.colors.warning60} !important`,
        },
      },
    },
  },
}));

const getAlignmentProps = (
  props: LayoutProps,
): {
  alignItems?: string;
  justifyContent?: string;
  flexWrap?: 'wrap';
  flexDirection: 'row' | 'column';
} => {
  const {
    horizontal,
    wrap,
    adjustCenter,
    adjustLeft,
    adjustRight,
    adjustTop,
    adjustBottom,
  } = props;
  const horAlignment = adjustLeft
    ? 'flex-start'
    : adjustRight
      ? 'flex-end'
      : adjustCenter
        ? 'center'
        : 'space-between';
  const vertAlignment = adjustTop
    ? 'flex-start'
    : adjustBottom
      ? 'flex-end'
      : adjustCenter
        ? 'center'
        : undefined;
  const flexWrap: BoxProps['flexWrap'] = wrap ? 'wrap' : undefined;
  return horizontal
    ? {
        alignItems: vertAlignment,
        justifyContent: horAlignment,
        flexWrap,
        flexDirection: 'row' as const,
      }
    : {
        alignItems: horAlignment,
        justifyContent: vertAlignment,
        flexWrap,
        flexDirection: 'column' as const,
      };
};

const getClassName = (props: LayoutProps) => {
  const {hidden, focusable, error, warning} = props;

  return hidden
    ? focusable
      ? 'Ebba-hidden Ebba-focusable'
      : 'Ebba-hidden'
    : focusable
      ? error
        ? 'Ebba-focusable Ebba-focusable-error'
        : warning
          ? 'Ebba-focusable Ebba-focusable-warning'
          : 'Ebba-focusable'
      : undefined;
};

const useDerivedLayoutProps = (props: LayoutProps) => {
  // Resolve to a MUI palette path (e.g. 'primary.main'); the sx system maps it
  // to the corresponding theme palette color.
  const themeColor = props.kind
    ? `${props.kind}.${props.colorMode ?? 'main'}`
    : undefined;

  return useMemo(() => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      kind,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      colorMode,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      hidden,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      focusable,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      error,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      warning,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      horizontal,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      wrap,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      adjustCenter,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      adjustLeft,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      adjustRight,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      adjustTop,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      adjustBottom,
      ...otherProps
    } = props;

    // MUI v9 Box no longer handles system props, so fold them into sx here
    return extendSxProp({
      backgroundColor: themeColor,
      className: getClassName(props),
      display: 'flex',
      ...getAlignmentProps(props),
      ...otherProps,
    });
  }, [props, themeColor]);
};

export const Layout: FCWithChildren<LayoutProps> = props => {
  const derivedProps = useDerivedLayoutProps(props);

  return <MuiBoxStyled {...derivedProps} />;
};

type InteractiveLayoutProps = LayoutProps & {
  onClick?: () => void;
  /** Emulates blur using focusin and focusout events */
  onBlur?: () => void;
};

export const InteractiveLayout: React.FC<InteractiveLayoutProps> = ({
  onClick,
  onBlur,
  ...layoutProps
}) => {
  const ref = useRef<Document>(document);

  const derivedProps = useDerivedLayoutProps(layoutProps);

  const handleClick = useCallback(() => {
    onClick && onClick();
  }, [onClick]);

  useEventListener('click', handleClick, ref);

  const [hasFocus, setHasFocus] = useState<boolean | undefined>(undefined);

  const handleOnFocusOut = useCallback(() => {
    // console.log('handleOnFocusOut');
    onBlur && setHasFocus(false);
  }, [onBlur]);

  useEventListener('focusout', handleOnFocusOut, ref);

  const handleOnFocusIn = useCallback(() => {
    // console.log('handleOnFocusIn');
    onBlur && setHasFocus(true);
  }, [onBlur]);

  useEventListener('focusin', handleOnFocusIn, ref);

  const delayedBlurTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!onBlur) {
      return;
    }

    if (delayedBlurTimerRef.current) {
      clearTimeout(delayedBlurTimerRef.current);
    }

    if (hasFocus === false) {
      delayedBlurTimerRef.current = setTimeout(() => {
        // console.log('hasFocus', hasFocus);
        onBlur && onBlur();
      }, 100);
    }
    // Only trigger on hasFocus changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFocus]);

  return <MuiBoxStyled ref={ref} {...derivedProps} />;
};
